import React, {
    useState,
    useEffect,
    useContext,
    useCallback,
    useMemo,
} from 'react'
import { Grid, Button, Box } from '@mui/material'
import { AnyInput, useValidatedForm } from '../anyform'
import {
    getSetupPassword,
    getSignupUser,
    setupPassword,
    signupUser,
} from '../api'
import { GoHome, ExternSurface, Progress } from '../components'
import { AppContext } from '../context'
import { FeedbackContext } from '../feedback'
import { translate } from '../i18n'
import { SetupPasswordRequest, User } from '../model'
import { RouterParams } from '../router'
import { ConfirmPasswordValidator } from '../validators'

export default function SetupPassword({ match }: RouterParams) {
    const context = useContext(AppContext)
    const feedbackContext = useContext(FeedbackContext)
    const isSignup = window.location.pathname.startsWith('/signup')
    const code = match.params.code

    const [status, setStatus] = useState<string>('loading')
    const [email, setEmail] = useState<string>()
    const [password, setPassword] = useState<string>()
    const { bind, register, isValid } = useValidatedForm<SetupPasswordRequest>()
    const validators = useMemo(() => {
        return [new ConfirmPasswordValidator(() => password ?? '')]
    }, [password])

    const retrievePromise = useCallback((): Promise<User> => {
        if (isSignup) {
            return getSignupUser(code)
        }
        return getSetupPassword(code)
    }, [isSignup, code])

    const submitPromise = (): Promise<User> => {
        const request = bind()
        if (isSignup) {
            return signupUser(code, request)
        }
        return setupPassword(code, request)
    }

    useEffect(() => {
        setStatus('loading')
        retrievePromise()
            .then((response) => {
                setEmail(response.email)
                setStatus('loaded')
            })
            .catch((error) => {
                setStatus(error.message)
            })
    }, [retrievePromise])

    const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (!isValid()) {
            return
        }

        feedbackContext.showBackdrop()
        submitPromise()
            .then((_) => {
                context.onSignedIn()
            })
            .catch((error) => {
                feedbackContext.showError(error.message)
            })
            .finally(() => {
                feedbackContext.closeBackdrop()
            })
    }

    if (status === 'loading') {
        return <Progress />
    }

    if (status !== 'loaded') {
        return <GoHome message={status} />
    }

    return (
        <ExternSurface
            title={
                translate(
                    isSignup
                        ? 'auth.activate.title'
                        : 'auth.setup_password.title'
                ) as string
            }
            description={
                translate(
                    isSignup ? 'auth.activate.text' : 'auth.setup_password.text'
                ) as string
            }
        >
            <form autoComplete="off" noValidate onSubmit={onSubmit}>
                <input type="hidden" id="email" name="email" value={email} />
                <Grid
                    container
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Grid item xs={12}>
                        <AnyInput
                            type="password"
                            name="password"
                            label={translate('users.password') as string}
                            required
                            hiddenLabel
                            register={register}
                            notifier={(name, value) =>
                                setPassword(`${value ?? ''}`)
                            }
                        />
                        <AnyInput
                            type="password"
                            name="confirm_password"
                            label={
                                translate('users.confirm_password') as string
                            }
                            required
                            register={register}
                            validators={validators}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Box my={2}>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                fullWidth
                                size="large"
                            >
                                {translate(
                                    isSignup
                                        ? 'auth.activate.button'
                                        : 'auth.setup_password.button'
                                )}
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </form>
        </ExternSurface>
    )
}
